import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Card, Button, Badge } from "react-bootstrap";
import { BiPlus, BiLoader } from "react-icons/bi";
import { useSelector, useDispatch } from "react-redux";
import { makeRequest } from "../../utils/makeRequest";
import * as urls from '../../utils/urlConst';
import toastNotify from "../../utils/toastNotify";
import Loader from "../../components/Loader";
import { handleReCallTicketDetails } from "../../redux/ticket";
import Textfield from "../../components/textField/TextField";
import SelectField from "../../components/textField/SelectField";
import { Formik } from "formik";
import { exceptionLog } from "../../utils/common";

type ticketDataProps = {
  CategoryName: string,
  ClassificationCode: string,
  CreateBy: string,
  CreateDate: string,
  LastActivity: string,
  LastReplier: string,
  Priority: string,
  ReplayCount: string,
  StatusCode: string,
  StatusName: string,
  SubCategoryName: string,
  Subject: string,
  TicketActive: string,
  TicketId: string,
  TicketType: string,
  TranCode: string,
  ReadStatus: string,
}

type statusDataProps = {
  StatusFor: string,
  StatusName: string,
  StatusType: string,
  TranCode: string,
}

const Tickets = () => {
  const navigate = useNavigate();
  const screenWidth = useSelector((state: any) => state.layout.currentScreenWidth);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [isSearchLoader, setIsSearchLoader] = useState<boolean>(false);
  const [isResetBtn, setIsResetBtn] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<ticketDataProps[]>([]);
  const [getStatusData, setGetStatusData] = useState<statusDataProps[]>([]);
  const dispatch = useDispatch();
  const { state } = useLocation();

  // --------------------------------------------- this function called ticket list API start -------------------------------------------------

  const fetchTicketList = async (loaderflag: string, val?: any, resetForm?: any) => {
    try {
      if (loaderflag === "getData") {
        setIsLoader(true);
      } else {
        setIsSearchLoader(true)
      }
      const payload = {
        TicketId: val !== undefined ? val.ticketId : "",
        StatusCode: val !== undefined ? val.statusCD : "All",
        StatusType: val !== undefined ? val.statusType : "",
        ActionType: "T",
        DashboardTktStatus: "",
      };

      const config = {};
      await makeRequest('POST', urls.getTicket, payload, config).then((result) => {
        if (result.STATUS === '0') {
          const response = result.RESPONSE;
          setTicketData(response);
          setIsResetBtn(false)
        } else {
          setTicketData([]);
          setIsResetBtn(false);
          result.MESSAGE !== "No Data Found" && toastNotify(result.MESSAGE, 'error');
        }
      })
    } catch (error: any) {
      if (error.status !== 403) {
        await exceptionLog(error, "Get ticket", "N");
      }
    } finally {
      if (loaderflag === "getData") {
        setIsLoader(false);
      } else {
        setIsSearchLoader(false);
      }
    }
  };

  // --------------------------------------------- this function called ticket list API end -------------------------------------------------


  // --------------------------------------------- this function called status dropdown API start -------------------------------------------------

  const fetchStatus = async () => {
    try {
      const payload = {};
      const config = {};
      await makeRequest('POST', urls.getTicketStatus, payload, config).then((result) => {
        if (result.STATUS === '0') {
          const response = result.RESPONSE;
          setGetStatusData(response);
        } else {
          setGetStatusData([]);
          result.MESSAGE !== "No Data Found" && toastNotify(result.MESSAGE, 'error');
        }
      })
    } catch (error: any) {
      if (error.status !== 403) {
        await exceptionLog(error, "Get ticket status", "N");
      }
    }
  }

  // --------------------------------------------- this function called status dropdown API end -------------------------------------------------

  useEffect(() => {
    const handleData = async () => {
      if (state?.length !== 0 && state !== null) {
        setTicketData(state);
        setIsResetBtn(true)
      } else {
        await fetchTicketList("getData", undefined, undefined);
      };
      await fetchStatus();
    };
    handleData();
  }, [])

  // --------------------------------------------- this function called Ticket Details API start -------------------------------------------------

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      setIsLoader(true);
      const payload = {
        TicketId: ticketId,
      };
      const config = {};
      await makeRequest('POST', urls.getTicketDetails, payload, config).then((result) => {
        if (result.STATUS === '0') {
          const response = result.RESPONSE[0];
          navigate('/tickets/viewdetails', {
            state: {
              response: { ...response, TicketData: ticketId },
            }
          })
        } else {
          result.MESSAGE !== "No Data Found" && toastNotify(result.MESSAGE, 'error');
        }
      })
    } catch (error: any) {
      if (error.status !== 403) {
        await exceptionLog(error, "Get ticket details", "N");
      }
    } finally {
      setIsLoader(false);
    }
  };

  // --------------------------------------------- this function called Ticket Details API end -------------------------------------------------

  useEffect(() => {
    dispatch(handleReCallTicketDetails(fetchTicketDetails));
  }, [])

  return (
    <div>
      <Row className="mb-3">
        <Col md={6} className="d-flex align-items-center">
          <h2 className="text-base mb-0 fw-semibold">Tickets</h2>
          <div className='d-block d-md-none ms-auto'>
            <Button variant="orange" className='btn-sm d-flex align-items-center ms-auto' onClick={() => navigate('/tickets/createticket')}><BiPlus className='text-lg me-1' />Create Ticket</Button>
          </div>
        </Col>
        <Col md={6} className="d-block d-md-flex align-items-center gap-2 jutify-content-end mt-2 mt-md-0">
          <Button variant="orange" className='btn-sm d-none d-md-block ms-md-auto ms-0' onClick={() => navigate('/tickets/createticket')}><BiPlus className='text-lg me-1' />Create Ticket</Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Formik
            initialValues={{ ticketId: '', statusCD: "All", statusLabel: "All (Not include close)", statusType: "" }}
            onSubmit={(values, { resetForm }) => {
              fetchTicketList("search", values, resetForm)
            }}
          >
            {({ values, handleSubmit, handleChange, handleBlur, setFieldValue }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={3}>
                      <Textfield
                        label="Ticket Id"
                        name="ticketId"
                        id="ticketId"
                        size="small"
                        maxLength={200}
                        value={values.ticketId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Col>
                    <Col md={3} className="mt-3 mt-md-0">
                      <SelectField
                        name="status"
                        required
                        options={[{ value: "", label: 'All', StatusType: "", StatusFor: "" }, { value: "All", label: 'All (Not include close)', StatusType: "", StatusFor: "" },
                        ...getStatusData.map((items) => ({
                          value: items.TranCode, label: items.StatusName, StatusType: items.StatusType, StatusFor: items.StatusFor
                        }))
                        ]}
                        value={{ value: values.statusCD, label: values.statusLabel, StatusType: values.statusType }}
                        placeholder='Status'
                        onChange={(e: any) => { setFieldValue("statusCD", e?.value); setFieldValue("statusLabel", e?.label); setFieldValue("statusType", e?.StatusType) }}
                      />
                    </Col>
                    <Col md={3} className="mt-3 mt-md-0">
                      <Button type="submit" variant="orange" className='btn-sm' disabled={!isResetBtn && isSearchLoader}>{!isResetBtn ? !isSearchLoader ? 'Search' : <><BiLoader className='icon-loader text-white text-lg' /> Loading...</> : 'Search'}</Button>
                      {isResetBtn &&
                        <Button type="submit" variant="danger" className='btn-sm ms-2'>Reset</Button>
                      }
                    </Col>
                  </Row>
                </form>
              )
            }}
          </Formik>
        </Card.Body>
      </Card>

      <Card className='ticket-card mt-2'>
        <Card.Body className={`p-0 ${ticketData.length === 0 && 'd-flex justify-content-center align-items-center'}`}>
          {!isLoader ? <>
            {ticketData.length !== 0 ?
              <div>
                {ticketData.map((item: any, id: any) => (
                  <div
                    key={id}
                    className={`w-100 d-flex border-bottom py-2 px-3 ticketRow align-items-center ${item.ReadStatus === "N" ? "ticket-unread" : ""}`}
                    onClick={() => fetchTicketDetails(item.TicketId)}
                  >
                    <div>
                      {item.Priority === 'H' && <div style={{ width: 40, height: 40, borderRadius: "100%" }} className='d-flex justify-content-center align-items-center badge bg-danger me-2'>HI</div>}
                      {item.Priority === 'M' && <div style={{ width: 40, height: 40, borderRadius: "100%" }} className='d-flex justify-content-center align-items-center badge bg-warning me-2'>MD</div>}
                      {item.Priority === 'L' && <div style={{ width: 40, height: 40, borderRadius: "100%" }} className='d-flex justify-content-center align-items-center badge bg-success me-2'>LW</div>}
                    </div>

                    <div>
                      <div className={`d-flex  ${screenWidth <= 600 ? "flex-column" : "align-items-center"}`}>
                        <span className="text-slate-700 ms-1 text-sm">
                          #{item.TicketId}
                        </span>
                        <div
                          className={`text-base mb-0 ms-2 text-slate-700 ${screenWidth <= 600 && "ticketSubJect"}`}
                        >
                          {item.Subject}
                        </div>
                      </div>

                      {screenWidth >= 600 &&
                        <div className="d-flex align-items-center">
                          <div className="border-end pe-2">
                            <div className="text-xs text-slate-500">Category</div>
                            <div className="text-xs">{item.CategoryName}</div>
                          </div>
                          {item.SubCategoryName !== "" &&
                            <div className="border-end px-2">
                              <div className="text-xs text-slate-500">Sub Category</div>
                              <div className="text-xs">{item.SubCategoryName}</div>
                            </div>
                          }

                          <div className="border-end px-2">
                            <div className="text-xs text-slate-500">Category Type</div>
                            {item.TicketType === "S" && (
                              <div className="text-xs">Service</div>
                            )}
                            {item.TicketType === "T" && (
                              <div className="text-xs">Transaction</div>
                            )}
                          </div>
                          <div className="border-end px-2">
                            <div className="text-xs text-slate-500">Last Activity</div>
                            <div className="text-xs">{item.LastActivity}</div>
                          </div>
                          <div className="border-end px-2">
                            <div className="text-xs text-slate-500">Replies</div>
                            <div className="text-xs">{item.ReplayCount} Reply</div>
                          </div>
                          <div className="px-2">
                            <div className="text-xs text-slate-500">Create Date</div>
                            <div className="text-xs">{item.CreateDate}</div>
                          </div>
                        </div>
                      }
                    </div>

                    <div className={`ms-auto  d-flex gap-2 align-items-center`}>
                      <div>
                        {item.StatusName === 'O' && <Badge bg="success">Open</Badge>}
                        {item.StatusName === 'C' && <Badge bg="danger">Closed</Badge>}
                        {item.StatusName === 'P' && <Badge bg="primary">Pending</Badge>}
                        {item.StatusName === 'I' && <Badge bg="warning">In-Process</Badge>}
                        {item.StatusName === 'R' && <Badge bg="success">Re-Open</Badge>}
                        {item.StatusName === 'A' && <Badge bg="info">Assigned</Badge>}
                        {item.StatusName !== 'O' && item.StatusName !== 'C' && item.StatusName !== 'P' && item.StatusName !== 'I' && item.StatusName !== 'R' && item.StatusName !== 'A' ? <Badge bg="warning">{item.StatusName}</Badge> : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              :
              <div className="d-flex flex-column justify-content-center align-items-center">
                <img src="/imgaes/svg/55024593_9264820.svg" alt="No Data Found" style={{ width: 200 }} />
                <h1 className="text-base text-center">No complaint ticket found. You can create a new complaint ticket here.</h1>
              </div>
            }</>
            : <div className='d-flex justify-content-center align-items-center h-100'>
              <Loader />
            </div>
          }
        </Card.Body>
      </Card >
    </div >
  );
};

export default Tickets;
